import { FolderScope } from '@/enums/folder-scope.enum';
import { log } from '@/logger/logger.ts';
import { getAxiosInstance } from '@/services/axiosConfig';
import type { DashboardRecipe } from '@/types/recipes';

const logger = log.getLogger('getRecipes');
const axiosInstance = getAxiosInstance();

/** Matches GET /v1/recipes/{recipeId}/fonts (CustomFontResponseDto). */
export type WorkspaceCustomFontDto = {
  id: string;
  workspaceId: string;
  weight: number;
  isItalic: boolean;
  displayName: string;
  fontFamilyKey: string;
  url: string;
  originalFileName: string | null;
  createdAt: string;
  updatedAt: string;
};

export const getRecipeFonts = async (recipeId: string): Promise<WorkspaceCustomFontDto[]> => {
  try {
    const response = await axiosInstance.get<WorkspaceCustomFontDto[]>(`/v1/recipes/${recipeId}/fonts`);
    return response.data;
  } catch (error) {
    logger.error('Error fetching recipe fonts:', error);
    throw error;
  }
};

export const getUserRecipes = async (): Promise<DashboardRecipe[]> => {
  try {
    const response = await axiosInstance.get<DashboardRecipe[]>('/v1/recipes');
    return response.data;
  } catch (error) {
    logger.error('Error fetching user recipes:', error);
    throw error;
  }
};

export const getRecipePoster = async (id: string): Promise<string | undefined> => {
  try {
    const response = await axiosInstance.get<{ poster?: string }>(`/v1/recipes/${id}`);
    return response.data.poster;
  } catch (error) {
    logger.error('Error fetching recipe poster:', error);
    return undefined;
  }
};

export const getPublicRecipes = async (): Promise<DashboardRecipe[]> => {
  try {
    const response = await axiosInstance.get<DashboardRecipe[]>('/v1/recipes/public');
    return response.data;
  } catch (error) {
    logger.error('Error fetching user recipes:', error);
    throw error;
  }
};

export const getUserSharedApps = async () => {
  try {
    const userSharedAppsResponse = await axiosInstance.get<DashboardRecipe[]>('/v1/recipes/shared');
    const sharedApps = userSharedAppsResponse.data.filter((recipe) => recipe.isApp === true);
    const sharedWithCurrentUser = userSharedAppsResponse.data.filter((recipe) => recipe.isApp !== true);
    return { sharedApps, sharedWithCurrentUser };
  } catch (error) {
    logger.error('Error fetching shared apps:', error);
    throw error;
  }
};

export const createRecipe = async (parentId?: string | null, scope?: FolderScope) => {
  try {
    const response = await axiosInstance.post<DashboardRecipe>('/v1/recipes/create', {
      ...(parentId && { folderId: parentId }),
      ...(scope && { scope }),
    });

    return response.data;
  } catch (error) {
    logger.error('Error creating recipe:', error);
    throw error;
  }
};

export const duplicateRecipe = async (id: string) => {
  try {
    const response = await axiosInstance.post<DashboardRecipe>(`/v1/recipes/${id}/duplicate`);
    return response.data;
  } catch (error) {
    logger.error('Error duplicating recipe:', error);
    throw error;
  }
};

export const deleteRecipe = async (id: string, isShared: boolean) => {
  try {
    if (isShared) {
      await axiosInstance.post(`/v1/recipes/${id}/shared/delete`);
    } else {
      await axiosInstance.delete(`/v1/recipes/${id}`);
    }
  } catch (error) {
    logger.error('Error deleting recipe:', error);
    throw error;
  }
};

export const renameRecipe = async (id: string, newName: string): Promise<{ recipeUpdatedAt: string }> => {
  try {
    const response = await axiosInstance.put<{ recipeUpdatedAt: string }>(`/v1/recipes/${id}`, {
      name: newName,
    });
    return response.data;
  } catch (error) {
    logger.error('Error renaming recipe:', error);
    throw error;
  }
};

export const changeOwner = async (id: string, newOwnerId: string) => {
  try {
    await axiosInstance.put(`/v1/recipes/${id}`, {
      owner: newOwnerId,
    });
  } catch (error) {
    logger.error('Error changing owner:', error);
    throw error;
  }
};

export const restoreWorkflows = async (workflowIds: string[]): Promise<void> => {
  try {
    await axiosInstance.patch('/v1/workflows/restore', { workflowIds });
  } catch (e) {
    logger.error('Error restoring workflows:', e);
    throw e;
  }
};
